import { Link } from "react-router-dom";
import { useEffect } from "react";

const LOGO_TEXT = "https://customer-assets.emergentagent.com/job_bump-app-site/artifacts/g91mm2s9_b%20logo%20name.png";

const LegalNav = () => (
  <nav className="border-b border-gray-100 bg-white sticky top-0 z-40">
    <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-16">
      <Link to="/" className="flex items-center" data-testid="nav-logo">
        <img src={LOGO_TEXT} alt="Buumped" className="h-8" />
      </Link>
      <Link to="/" className="text-sm text-gray-500 hover:text-[#F07167]" data-testid="back-to-home">Back to Home</Link>
    </div>
  </nav>
);

export default function CookiePolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-white min-h-screen" data-testid="cookie-policy-page">
      <LegalNav />
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
        <p className="text-sm text-gray-400 mb-4">Last updated: January 2026</p>
        <p className="text-sm text-gray-500 mb-12 italic">This Cookie Policy explains how Buumped uses cookies and similar tracking technologies. It should be read alongside our <Link to="/privacy" className="text-[#F07167] underline">Privacy Policy</Link>.</p>

        <div className="prose prose-gray max-w-none space-y-10 text-gray-600 leading-relaxed text-[15px]">

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. What Are Cookies &amp; Similar Technologies</h2>
            <p>Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently, to provide a better user experience, and to give website operators information about how their site is being used.</p>
            <p>Similar technologies include web beacons (pixel tags), local storage, SDKs (software development kits), and device fingerprinting. In this policy, we refer to all of these technologies collectively as "cookies."</p>
            <p>For questions about our use of cookies, contact us at <a href="mailto:info@buumped.com" className="text-[#F07167] underline">info@buumped.com</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Types of Cookies We Use</h2>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">Strictly Necessary Cookies</h3>
            <p>These cookies are essential for the website and app to function properly. They cannot be disabled. They include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Authentication and session management</li>
              <li>Security and fraud prevention</li>
              <li>Load balancing and server functionality</li>
              <li>Cookie consent preferences storage</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">Legal basis: Necessary for the performance of our contract with you and our legitimate interests in providing a secure service.</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">Functional Cookies</h3>
            <p>These cookies enable enhanced functionality and personalisation. They may be set by us or by third-party providers whose services we use. They include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Language and region preferences</li>
              <li>User interface customisation and settings</li>
              <li>Previously viewed content and preferences</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">Legal basis: Your consent. You can manage these via cookie settings.</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">Analytics &amp; Performance Cookies</h3>
            <p>These cookies help us understand how visitors interact with our website and app by collecting information anonymously. This helps us improve our Service. They include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Page visit counts and traffic sources</li>
              <li>Feature usage patterns and navigation flows</li>
              <li>Error tracking and performance monitoring</li>
              <li>A/B testing and service improvements</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">Legal basis: Your consent. You can manage these via cookie settings.</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">Advertising &amp; Marketing Cookies</h3>
            <p>These cookies may be set through our Service by our advertising partners. They are used to build a profile of your interests and show you relevant advertisements on other sites. They include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Social media integration and sharing features</li>
              <li>Targeted and retargeted advertising</li>
              <li>Campaign measurement and attribution</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">Legal basis: Your consent. You can manage these via cookie settings.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. Third-Party Cookies</h2>
            <p>Some cookies on our Service are placed by third parties. We do not control how these third parties use their cookies. Third-party services we may use include:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Analytics providers</strong> (e.g., Google Analytics) — to analyse website traffic and user behaviour</li>
              <li><strong>Social media platforms</strong> (e.g., Facebook, Instagram, TikTok) — to enable social sharing features and measure the effectiveness of our social media campaigns</li>
              <li><strong>Advertising networks</strong> — to deliver relevant advertisements and measure campaign performance</li>
            </ul>
            <p>Each third party operates under its own privacy and cookie policies. We encourage you to review their policies.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. Managing Your Cookie Preferences</h2>
            <p>You have several options to manage cookies:</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Cookie Consent Banner</h3>
            <p>When you first visit our website, a cookie banner will appear allowing you to accept or decline non-essential cookies. You can change your preferences at any time by clearing your browser cookies and revisiting the site.</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Browser Settings</h3>
            <p>Most web browsers allow you to control cookies through their settings. You can typically:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>View what cookies are stored on your device</li>
              <li>Delete some or all cookies</li>
              <li>Block all cookies or only third-party cookies</li>
              <li>Set your browser to notify you when a cookie is being set</li>
            </ul>
            <p>Common browser cookie settings:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Apple Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Microsoft Edge</a></li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Opt-Out Tools</h3>
            <p>You can also opt out of certain third-party cookies using these tools:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Your Online Choices (EU)</a></li>
              <li><a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Digital Advertising Alliance (US)</a></li>
              <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#F07167] underline">Google Analytics Opt-Out</a></li>
            </ul>

            <p className="mt-4"><strong>Please note:</strong> Blocking or deleting cookies may affect the functionality of our Service. Essential cookies cannot be disabled as they are necessary for the Service to operate.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Cookie Retention Periods</h2>
            <p>Cookies have different lifespans:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Session cookies:</strong> Temporary cookies that are deleted when you close your browser.</li>
              <li><strong>Persistent cookies:</strong> Cookies that remain on your device for a set period (typically up to 12 months) or until you manually delete them.</li>
            </ul>
            <p>We review our cookie usage regularly to ensure cookies are not retained for longer than necessary.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Do Not Track Signals</h2>
            <p>Some browsers send "Do Not Track" (DNT) signals. There is currently no industry standard for how websites should respond to DNT signals. Our Service does not currently respond to DNT signals, but we honour your cookie consent preferences as described above.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Changes to This Cookie Policy</h2>
            <p>We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our practices. We will update the "Last updated" date at the top of this page. We encourage you to review this policy periodically.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Contact Us</h2>
            <p>If you have questions about our use of cookies or this Cookie Policy, please contact us:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Email: <a href="mailto:info@buumped.com" className="text-[#F07167] underline">info@buumped.com</a></li>
              <li>Phone: 020 3576 2766</li>
            </ul>
          </section>

        </div>

        <div className="mt-16 pt-8 border-t border-gray-100 flex gap-6 text-sm text-gray-400">
          <Link to="/privacy" className="hover:text-[#F07167]">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#F07167]">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}
